/**
 * Use the promotional rail to feature 5+ promoted stories that are related. For example, Beauty steals during a major event. Use when a higher prominence and/or creative image is needed.
 *
 * @module views/components/PromotionalRailGrid
 * @memberof -Common
 */
import './PromotionalRailGrid.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import PromotionalGrid from '@ulta/components/PromotionalGrid/PromotionalGrid';

/**
 * Represents a PromotionalRailGrid component
 *
 * @method
 * @param {PromotionalRailGridProps} props - React properties passed from composition
 * @returns PromotionalRailGrid
 */
export const PromotionalRailGrid = function( props ){
  const {
    /* Cards */
    dataCapture,
    items,

    /* Rail Settings */
    alignment = defaultProps.alignment,
    backgroundColor,
    isGrid,
    sliderConfig = defaultProps.sliderConfig,
    spaceBetween,

    /* Rail Title */
    subtitle,
    title,
    viewAllAction,

    /* Rail meta data */
    listPriceAccessibilityLabel,
    nextClickAction,
    previousClickAction,
    salePriceAccessibilityLabel,
    _railADAmeta,
    _railMeta
  } = props;

  if( !items ){
    return null;
  }

  return (
    <div
      className={ classNames( 'PromotionalRailGrid', {
        PromotionalRailGrid__withBackgroundColor: backgroundColor
      } ) }
    >
      { isGrid && (
        <PromotionalGrid
          backgroundColor={ backgroundColor }
          title={ title }
          subtitle={ subtitle }
          items={ items }
          alignment={ alignment }
        />
      ) }
      { !isGrid && (
        <GridContainer
          { ...spreadProps( {
            fullBleedContentBackgroundColor: backgroundColor
          } ) }
        >
          <Rail
            items={ items }
            viewAllAction={ viewAllAction }
            previousClickAction={ previousClickAction }
            nextClickAction={ nextClickAction }
            spaceBetween={ spaceBetween }
            dataCapture={ dataCapture }
            _railADAmeta={ _railADAmeta }
            _railMeta={ _railMeta }
            { ...spreadProps( sliderConfig ) }
          >
            { items?.length > 0 &&
              items?.map( ( module, moduleIndex ) => (
                <Slide key={ `Rail${moduleIndex + 1} ` }>
                  <AsyncComponent
                    { ...spreadProps( { ...module } ) }
                    listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
                    salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
                  />
                </Slide>
              ) ) }
          </Rail>
        </GridContainer>
      ) }
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {PromotionalRailGridProps}
 * @property {object} INTERSECTION_OBSERVER_OPTIONS - The default initial values for intersection observer.
 */
export const defaultProps = {
  alignment: 'bottom',
  sliderConfig: {
    sliderPerViewXL: 4,
    slidesPerGroupXL: 4,
    sliderPerViewLG: 3,
    slidesPerGroupLG: 3,
    sliderPerViewMD: 3,
    slidesPerGroupMD: 3,
    sliderPerViewSM: 1,
    slidesPerGroupSM: 1
  }
};
/**
 * Property type definitions
 *
 * @typedef PromotionalRailGridProps
 * @type {object}
 * @property {string} alignment - sets the alignment for the text.
 * @property {string} backgroundColor - Sets the backgroundColor
 * @property {boolean} isGrid - Sets the value of the component being a grid to true/false based on value
 * @property {array} items - Sets the Item array.
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label for screen readers
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {object} previousClickAction - The prop that holds the action for the previous button
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label for screen readers
 * @property {number} spaceBetween - sets the spaceBetween for the Rail
 * @property {string} subtitle - Sets the subtitle.
 * @property {string} title - Sets the headline title.
 * @property {object} viewAllAction - sets the url for action label.
 * @property {object} _railADAmeta - Sets carouselAccessibility
 * @property {object} _railMeta - Sets nextAccessibility, previousAccessibility
 */

export const propTypes = {
  /**
   * Sets the alignment for the text.
   */
  alignment: PropTypes.string,
  /**
   * Sets the backgroundColor
   */
  backgroundColor: PropTypes.string,
  /**
   * Sets the value of the component being a grid to true/false based on value
   */
  isGrid: PropTypes.bool,
  /**
   * Sets the Item array.
   */
  items: PropTypes.array,
  /**
   * Sets the list price accessibility label for screen readers
   */
  listPriceAccessibilityLabel: PropTypes.string,
  /**
   * The prop that holds the action for the next button.
   */
  nextClickAction: PropTypes.object,
  /**
   * The prop that holds the action for the previous button
   */
  previousClickAction: PropTypes.object,
  /**
   * Sets the sale price accessibility label for screen readers
   */
  salePriceAccessibilityLabel: PropTypes.string,
  /**
   * Sets the spaceBetween for the Rail
   */
  spaceBetween: PropTypes.number,
  /**
   * Sets the subtitle
   */
  subtitle: PropTypes.string,
  /**
   * Sets the headline title.
   */
  title: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
  /**
   * sets the url for action label.
   */
  viewAllAction: PropTypes.object,
  /**
   * Sets carouselAccessibility
   */
  _railADAmeta: PropTypes.object,
  /**
   * Sets nextAccessibility, previousAccessibility
   */
  _railMeta: PropTypes.object
};

PromotionalRailGrid.propTypes = propTypes;
PromotionalRailGrid.defaultProps = defaultProps;

export default PromotionalRailGrid;
